import { useEffect, useMemo, useRef, useState } from 'react';

type Props = {
  from: string;
  to: string;
  onChange: (range: { from: string; to: string }) => void;
  onClear?: () => void;
  className?: string;
  label?: string;
  allowedDates?: string[];
};

const DAYS = ['DOM', 'SEG', 'TER', 'QUA', 'QUI', 'SEX', 'SAB'];
const MONTHS = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];

function toLocalDate(value: string) {
  if (!value) return null;
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
}

function toIsoDate(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function formatRangeLabel(from: string, to: string) {
  if (!from && !to) return 'Selecione um período';
  const fmt = (value: string) => {
    const date = toLocalDate(value);
    return date ? date.toLocaleDateString('pt-BR') : '--';
  };
  if (from && to) return `${fmt(from)} - ${fmt(to)}`;
  if (from) return `Início: ${fmt(from)}`;
  return `Fim: ${fmt(to)}`;
}

function isSameDay(a: Date | null, b: Date | null) {
  return Boolean(a && b) && a!.getFullYear() === b!.getFullYear() && a!.getMonth() === b!.getMonth() && a!.getDate() === b!.getDate();
}

function buildCalendarGrid(viewMonth: Date) {
  const start = new Date(viewMonth.getFullYear(), viewMonth.getMonth(), 1);
  const startOffset = start.getDay();
  const gridStart = new Date(viewMonth.getFullYear(), viewMonth.getMonth(), 1 - startOffset);
  return Array.from({ length: 42 }, (_, index) => {
    const date = new Date(gridStart);
    date.setDate(gridStart.getDate() + index);
    return date;
  });
}

function getRecentAllowedDate(orderedAllowedDates: string[], ceiling: string) {
  return [...orderedAllowedDates].reverse().find((value) => value <= ceiling) ?? orderedAllowedDates[orderedAllowedDates.length - 1] ?? null;
}

export default function DateRangePicker({ from, to, onChange, onClear, className = '', label = 'Periodo', allowedDates }: Props) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const initialView = useMemo(() => toLocalDate(to) ?? toLocalDate(from) ?? new Date(), [from, to]);
  const [open, setOpen] = useState(false);
  const [activeField, setActiveField] = useState<'from' | 'to'>('from');
  const [viewMonth, setViewMonth] = useState(new Date(initialView.getFullYear(), initialView.getMonth(), 1));
  const allowedDateSet = useMemo(() => (allowedDates?.length ? new Set(allowedDates) : null), [allowedDates]);
  const orderedAllowedDates = useMemo(() => (allowedDates ? [...allowedDates].sort() : []), [allowedDates]);

  useEffect(() => {
    if (!open) return;
    const fallback = orderedAllowedDates.length ? toLocalDate(orderedAllowedDates[orderedAllowedDates.length - 1]) : null;
    const nextView = toLocalDate(activeField === 'from' ? from : to) ?? toLocalDate(from) ?? toLocalDate(to) ?? fallback ?? new Date();
    setViewMonth(new Date(nextView.getFullYear(), nextView.getMonth(), 1));
  }, [activeField, from, open, orderedAllowedDates, to]);

  useEffect(() => {
    if (!open) return;
    const handleClick = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open]);

  const fromDate = toLocalDate(from);
  const toDate = toLocalDate(to);
  const days = buildCalendarGrid(viewMonth);
  const todayIso = toIsoDate(new Date());

  function isDateAllowed(date: Date) {
    if (!allowedDateSet) return true;
    return allowedDateSet.has(toIsoDate(date));
  }

  function applyQuickRange(kind: 'today' | '7d' | 'all') {
    if (kind === 'all') {
      onChange({ from: '', to: '' });
      onClear?.();
      setActiveField('from');
      return;
    }
    if (orderedAllowedDates.length) {
      const fallbackDate = getRecentAllowedDate(orderedAllowedDates, todayIso);
      if (!fallbackDate) return;
      if (kind === 'today') {
        onChange({ from: fallbackDate, to: fallbackDate });
        setActiveField('to');
        return;
      }
      const start = new Date();
      start.setDate(start.getDate() - 6);
      const startLimit = toIsoDate(start);
      const rangeDates = orderedAllowedDates.filter((value) => value >= startLimit && value <= todayIso);
      if (rangeDates.length) {
        onChange({ from: rangeDates[0], to: rangeDates[rangeDates.length - 1] });
      } else {
        onChange({ from: fallbackDate, to: fallbackDate });
      }
      setActiveField('to');
      return;
    }
    const end = new Date();
    const start = new Date();
    if (kind === '7d') start.setDate(start.getDate() - 6);
    const next = { from: toIsoDate(start), to: toIsoDate(end) };
    onChange(next);
    setActiveField('to');
  }

  function selectDate(date: Date) {
    if (!isDateAllowed(date)) return;
    const iso = toIsoDate(date);
    if (activeField === 'from') {
      const nextTo = to && to < iso ? iso : to;
      onChange({ from: iso, to: nextTo });
      setActiveField('to');
      return;
    }
    const nextFrom = from && from > iso ? iso : from;
    onChange({ from: nextFrom, to: iso });
  }

  function cellClass(date: Date) {
    const iso = toIsoDate(date);
    const inMonth = date.getMonth() === viewMonth.getMonth();
    const selectedFrom = fromDate && isSameDay(date, fromDate);
    const selectedTo = toDate && isSameDay(date, toDate);
    const isSelected = selectedFrom || selectedTo;
    const isInRange = from && to && iso >= from && iso <= to;
    const isToday = iso === todayIso;
    const isDisabled = !isDateAllowed(date);
    return [
      'range-day',
      !inMonth ? 'range-day--muted' : '',
      isDisabled ? 'range-day--disabled' : '',
      isInRange ? 'range-day--range' : '',
      isSelected ? 'range-day--selected' : '',
      isToday ? 'range-day--today' : '',
    ].filter(Boolean).join(' ');
  }

  return (
    <div ref={rootRef} className={`relative min-w-[280px] ${className}`}>
      <div className="mb-1 text-xs font-semibold text-mbc-soft">{label}</div>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="range-trigger w-full"
      >
        <span className="flex items-center gap-2">
          <span className="range-trigger-icon" aria-hidden="true">▦</span>
          <span>{formatRangeLabel(from, to)}</span>
        </span>
        <span className={`text-xs transition-transform ${open ? 'rotate-180' : ''}`}>▼</span>
      </button>

      {open && (
        <div className="range-popover">
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setActiveField('from')}
              className={`range-bound ${activeField === 'from' ? 'range-bound--active range-bound--start' : ''}`}
            >
              Início: {from ? new Date(`${from}T12:00:00`).toLocaleDateString('pt-BR') : '--'}
            </button>
            <button
              type="button"
              onClick={() => setActiveField('to')}
              className={`range-bound ${activeField === 'to' ? 'range-bound--active range-bound--end' : ''}`}
            >
              Fim: {to ? new Date(`${to}T12:00:00`).toLocaleDateString('pt-BR') : '--'}
            </button>
          </div>

          <div className="mt-3 flex items-center gap-2">
            <button
              type="button"
              onClick={() => setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() - 1, 1))}
              className="range-nav"
            >
              ‹
            </button>
            <div className="range-month-title">
              {MONTHS[viewMonth.getMonth()]} de {viewMonth.getFullYear()}
            </div>
            <button
              type="button"
              onClick={() => setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 1))}
              className="range-nav"
            >
              ›
            </button>
          </div>

          <div className="mt-3 grid grid-cols-7 gap-1.5 text-center text-[11px] font-bold text-mbc-soft/85">
            {DAYS.map((day) => <div key={day}>{day}</div>)}
          </div>
          <div className="mt-2 grid grid-cols-7 gap-1.5">
            {days.map((date) => (
              <button
                key={date.toISOString()}
                type="button"
                onClick={() => selectDate(date)}
                className={cellClass(date)}
                disabled={!isDateAllowed(date)}
              >
                {date.getDate()}
              </button>
            ))}
          </div>

          <div className="mt-4 flex items-center justify-between gap-2">
            <button type="button" onClick={() => applyQuickRange('today')} className="range-quick">Hoje</button>
            <button type="button" onClick={() => applyQuickRange('7d')} className="range-quick">7 dias</button>
            <button
              type="button"
              onClick={() => {
                onChange({ from: '', to: '' });
                onClear?.();
              }}
              className="range-quick range-quick--outline"
            >
              Limpar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
